import { Link } from "react-router-dom";

export type Article = {
  id: string;
  title: string;
  category: string;
  categoryName: string;
  summary: string;
  cover: string;
  tags: string[];
  date: string;
  readCount: number;
  featured: boolean;
  hot: number;
  timeline?: { time: string; text: string }[];
  body?: string[];
};

type Props = {
  article: Article;
  variant?: "default" | "wide" | "compact";
  rank?: number;
};

const formatCount = (n: number) =>
  n >= 10000 ? `${(n / 10000).toFixed(1)} 万` : `${n}`;

export default function ArticleCard({ article, variant = "default", rank }: Props) {
  if (variant === "compact") {
    return (
      <Link
        to={`/gua/${article.id}`}
        className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 transition-colors hover:border-primary-300 hover:bg-primary-50"
      >
        {typeof rank === "number" && (
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-bold ${
              rank <= 3
                ? "bg-primary-500 text-background-50"
                : "bg-background-200 text-foreground-700"
            }`}
          >
            {rank}
          </span>
        )}
        <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 蝌蚪吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
            {article.title}
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            {article.categoryName} · 热度 {article.hot}
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/gua/${article.id}`}
        className="group grid grid-cols-1 gap-5 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:grid-cols-[280px_1fr]"
      >
        <div className="h-48 w-full overflow-hidden rounded-lg bg-background-200 sm:h-full">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 蝌蚪吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-center gap-3 py-2 pr-2">
          <span className="w-fit rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            {article.categoryName}
          </span>
          <h3 className="text-lg font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
            {article.title}
          </h3>
          <p className="line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(article.readCount)} 次回看
            </span>
            <span className="flex items-center gap-1 text-primary-600">
              <i className="ri-fire-line"></i>
              热度 {article.hot}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/gua/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title} - 蝌蚪吃瓜`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-3 py-1 text-xs font-medium text-primary-700 backdrop-blur">
          {article.categoryName}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {article.title}
        </h3>
        <p className="line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {article.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatCount(article.readCount)}
          </span>
        </div>
      </div>
    </Link>
  );
}